'use client';

import dynamic from 'next/dynamic';
import { Building2, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type {
  CompanyBasicResponse,
  CompanyContactResponse,
  CompanyFinancialsResponse,
  CompanyLegalStatus,
  CompanyNetworkResponse,
  InsolvencyRecordItem,
  ParentCompanyItem,
} from '@/lib/backend-api';
import {
  companyCountryCode,
  foundingDateAtPrecision,
  generateCompanySlug,
} from '@/lib/company-utils';
import { flagUrlForCountry } from '@/lib/country-flags';
import { localizePathname, type Locale } from '@/lib/i18n-routes';
import { generateIndustryUrl } from '@/lib/industry-utils';
import { getPublicStorageUrl } from '@/lib/public-storage-url';
import { useLocale } from '@/hooks/use-locale';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LocaleLink } from '@/components/locale-link';
import { CompanyCountryFlag } from '@/new_components/shared/CompanyCountryFlag';
import { AboutSection } from './AboutSection';
import {
  buildCompanyFactsSentence,
  formatCapturedDate,
  formatRegisterNumber,
  isoToGerman,
  ownershipSentenceKey,
  sizeClassSentenceKey,
} from './companyDetailUtils';
import { createFormatters } from './currencyFormatters';
import { AddToListMenu } from '@/new_components/lists/AddToListMenu';
import { SourceAttribution } from './SourceAttribution';
import { WatchlistButton } from './WatchlistButton';

/** Network diagram is heavy (XyFlow) — lazy-load + reserve fixed space to avoid CLS. */
const CompanyNetworkDiagram = dynamic(
  () =>
    import('./CompanyNetworkDiagram').then((mod) => mod.CompanyNetworkDiagram),
  {
    ssr: false,
    loading: () => (
      <div className="h-[228px]">
        <div className="mb-1 h-5" />
        <Skeleton className="h-[200px] w-full rounded-xl" />
      </div>
    ),
  },
);

/** Hero data already computed by the parent — passed in instead of recomputed here. */
export type CompanyProfileHeroData = {
  name: string;
  initial: string;
  euId: string;
  registerType: string | null;
  address: string | null;
  gruendungsDatum: string | null;
  gruendungsYear: string | null;
  gruendungsdokumentDatum: string | null;
  ersteEintragungDatum: string | null;
  gruendungsdokument: string | null;
  isDeleted: boolean;
};

export type CompanyProfileStatistics = Array<{ number: string; label: string }>;

/**
 * Hero / profile card at the top of the left column.
 *
 * Renders logo → name → EU-ID / address / founding year chips → status badges
 * (Deleted / Insolvent / In Liquidation / Dissolved) → parent-company link → key
 * statistics row,
 * then an About block (owner override > AI summary > Gegenstand), NACE tags, the
 * network diagram, business branches, products, risk/opportunity keywords, and
 * an AI disclaimer.
 *
 * `mobileSplit` cuts the card in two below `lg`: the card dissolves
 * (`contents`) and its top (header, statistics, About) and rest (network
 * onwards) become separate grid items with their own `order-*`, so the page
 * can put other cards between them. At `lg` both parts are `contents` and the
 * card is one box again.
 *
 * Logo precedence: contact.enrichment.logo_url > contact.favicon_url > basicData.logo_url > basicData.favicon_url.
 * Parent precedence: GLEIF LEI parent (authoritative ultimate owner) > Bundesanzeiger parent from financials.
 *
 * ⚠ `basicData.overrides.*` (company-owner overrides) always wins over register/AI/backfill
 * data for address fields and about_us — caller is responsible for producing `registerData`
 * with overrides already applied.
 */
export function CompanyProfileSection({
  basicData,
  registerData,
  financialsData,
  contactData,
  heroData,
  statistics,
  isLoadingBackground,
  euId,
  networkData,
  networkReloadToken,
  insolvencyRecords,
  legalStatus = 'active',
  isDeleted,
  className,
  compact = false,
  headerAction,
  mobileSplit,
}: {
  basicData: CompanyBasicResponse;
  registerData: CompanyRegisterResponseForProfile | null;
  financialsData: CompanyFinancialsResponse | null;
  contactData: CompanyContactResponse | null;
  heroData: CompanyProfileHeroData;
  statistics: CompanyProfileStatistics;
  isLoadingBackground?: boolean;
  euId: string;
  networkData?: CompanyNetworkResponse | null;
  /** Bump to make the network diagram refetch its graph (e.g. after the
   *  backend finishes parsing the Gesellschafterliste). */
  networkReloadToken?: number;
  insolvencyRecords?: InsolvencyRecordItem[];
  /** Resolved from the merged SI + CD + portal timeline. */
  legalStatus?: CompanyLegalStatus;
  isDeleted?: boolean;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
  /** Fit the profile inside the search preview without changing the full page. */
  compact?: boolean;
  /** Optional action after the watch/list buttons, used by the search preview. */
  headerAction?: React.ReactNode;
  /** Below `lg`, layout classes for the card's two halves — see above. */
  mobileSplit?: { top: string; rest: string };
}) {
  const { t, i18n } = useTranslation();
  const products = financialsData?.products || [];
  const businessBranches = financialsData?.business_branches || [];
  const naceCodes = registerData?.nace_codes || [];
  const { locale } = useLocale();
  const countryCode = companyCountryCode(heroData.registerType, heroData.euId);
  // Prefer the GLEIF LEI parent when present — it reflects the authoritative
  // ultimate owner edge. Fall back to the Bundesanzeiger parent derived from
  // participants/financials when no LEI parent exists.
  const parentCompany: ParentCompanyItem | undefined =
    basicData.lei_parent && basicData.lei_parent.name
      ? {
          eu_id: basicData.lei_parent.eu_id ?? null,
          name: basicData.lei_parent.name,
          sitz: basicData.lei_parent.city ?? null,
          register_id: basicData.lei_parent.register_id ?? null,
          register_type: basicData.lei_parent.register_type ?? null,
          register_court: basicData.lei_parent.register_court ?? null,
          country_code: basicData.lei_parent.country_code ?? null,
          lei_code: basicData.lei_parent.lei_code ?? null,
          source: 'lei',
        }
      : financialsData?.parent_company
        ? {
            ...financialsData.parent_company,
            country_code: financialsData.parent_company.country_code ?? countryCode,
            source: financialsData.parent_company.source ?? 'bundesanzeiger',
          }
        : undefined;
  const localizedAiSummary =
    i18n.language === 'de'
      ? financialsData?.ai_summary
      : financialsData?.ai_summary_en || financialsData?.ai_summary;

  // AI-extracted homepage summary (website_metadata) takes precedence over the
  // financial AI summary as the descriptive About paragraph. Localized DE/EN
  // with fallback. When it is the one being shown (i.e. no verified owner
  // override wins), the disclaimer cites the website + capture date instead.
  const websiteMeta = basicData.website_metadata;
  const localizedWebsiteSummary =
    (i18n.language === 'de'
      ? websiteMeta?.homepage_summary_de
      : websiteMeta?.homepage_summary_en || websiteMeta?.homepage_summary_de) ||
    null;
  const usingWebsiteSummary =
    !basicData?.overrides?.about_us?.trim() &&
    !localizedAiSummary?.trim() &&
    !!localizedWebsiteSummary?.trim();
  const websiteCapturedDate = formatCapturedDate(
    websiteMeta?.captured_at,
    i18n.language,
  );

  // Family / succession verdict as prose — appended to the About summary
  // instead of appearing as badges in the FounderProfileCard.
  const ownershipKey = ownershipSentenceKey(basicData.founder_profile);
  const ownershipSentence = ownershipKey
    ? (t(ownershipKey, { name: basicData.display_name }) as string)
    : null;

  // § 267 size class, in the same prose rather than a card of its own. For a
  // company that files no P&L this is the only thing the register lets us say
  // about its size, and it is indexable where a badge would not be.
  // HGB size classes do not apply to an Austrian company (§ 221 UGB).
  const sizeKey =
    countryCode === 'AT'
      ? null
      : sizeClassSentenceKey(financialsData?.metrics?.[0]);
  const sizeSentence = sizeKey ? (t(sizeKey) as string) : null;

  // Deterministic factual lead for the About block — always present so even
  // pages without an AI summary or Gegenstand carry unique, crawlable prose.
  const latestMetric = financialsData?.metrics?.[0];
  const latestEmployee =
    financialsData?.employee_data?.find((e) => e.total != null) ??
    financialsData?.employee_data?.[0];
  const factsLead = buildCompanyFactsSentence(
    {
      name: basicData.display_name,
      city: basicData.sitz,
      registerCourt: basicData.register_court,
      registerType: basicData.register_type,
      registerId: basicData.register_id,
      foundingYear: heroData.gruendungsYear,
      latestRevenue: latestMetric?.total_revenue ?? null,
      latestRevenueYear: latestMetric?.year ?? null,
      latestEmployees: latestEmployee?.total ?? null,
    },
    t,
    createFormatters(i18n.language),
  );
  const logoUrl =
    getPublicStorageUrl(contactData?.enrichment?.logo_url) ??
    getPublicStorageUrl(contactData?.favicon_url) ??
    getPublicStorageUrl(basicData.logo_url) ??
    getPublicStorageUrl(basicData.favicon_url) ??
    null;

  // In compact mode the card must not bound the sticky header: its containing
  // block spans the entire preview, including the people and document cards.
  const split = mobileSplit && !compact ? mobileSplit : null;

  return (
    <Card variant="grid" className={compact ? 'contents' : cn(className, split && 'max-lg:contents')}>
      <div className={split ? cn(split.top, 'lg:contents') : 'contents'}>
      <CardHeader className={compact ? "sticky top-0 z-20 bg-background py-3 px-5 min-h-0 border-b border-border flex-nowrap gap-3" : "py-3 px-5 min-h-0 border-b-0"}>
        <div className={compact ? "flex min-w-0 flex-1 items-center gap-3" : "flex items-center gap-3"}>
          {logoUrl && (
            <span className="inline-flex h-[2.75em] w-[2.75em] items-center justify-center rounded-lg bg-white/95 dark:bg-white flex-shrink-0 overflow-hidden">
              <img
                src={logoUrl}
                alt={`${heroData.name} logo`}
                className="h-full w-full object-contain"
                style={{ imageRendering: 'auto' }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </span>
          )}
          <div className={compact ? "flex min-w-0 flex-col gap-0.5" : "flex flex-col gap-0.5"}>
            <h1 className={compact ? "break-words text-lg font-bold tracking-tight text-foreground" : "text-base sm:text-2xl font-bold tracking-tight text-foreground"}>
              {heroData.name}
            </h1>
            <span className="text-[9px] sm:text-xs font-medium text-muted-foreground flex flex-wrap items-center gap-x-1.5">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-default">{heroData.euId}</span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t(
                      'companyDetail.euIdTooltip',
                      'European Unique Identifier',
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className="text-muted-foreground/50 text-sm leading-none select-none">
                |
              </span>
              {heroData.address && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-default">
                        {heroData.address}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      {t(
                        'companyDetail.businessAddress',
                        'Geschäftsanschrift',
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {/* Country name, for German and Swiss companies alike — the
                  address alone doesn't say which register this is. No flag. */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex cursor-default">
                      <CompanyCountryFlag countryCode={countryCode} showName hideFlag />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t('companyDetail.countryTooltip')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {heroData.gruendungsYear && (
                <>
                  <span className="text-muted-foreground/50 text-sm leading-none select-none">
                    |
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">
                          {heroData.gruendungsYear}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="text-xs">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-semibold text-center">
                            {t('companyDetail.foundingDate', 'Gründungsdatum')}
                          </span>
                          {heroData.gruendungsdokumentDatum && (
                            <span className="tabular-nums">
                              {isoToGerman(
                                foundingDateAtPrecision(
                                  heroData.gruendungsdokumentDatum,
                                ),
                              )}
                              :{' '}
                              {heroData.gruendungsdokument === 'Satzung'
                                ? t('companyDetail.satzung', 'Satzung')
                                : t(
                                    'companyDetail.articlesOfAssociation',
                                    'Gesellschaftsvertrag',
                                  )}
                            </span>
                          )}
                          {heroData.ersteEintragungDatum && (
                            <span className="tabular-nums">
                              {isoToGerman(
                                foundingDateAtPrecision(
                                  heroData.ersteEintragungDatum,
                                ),
                              )}
                              :{' '}
                              {t(
                                'companyDetail.firstRegistration',
                                'Erste Eintragung',
                              )}
                            </span>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
              {isDeleted && (
                <>
                  <span className="text-muted-foreground/50 text-sm leading-none select-none">
                    |
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center rounded-full bg-destructive/10 border border-destructive/30 px-1.5 py-0.5 font-medium text-destructive cursor-default">
                          {t('companyDetail.deletedLabel', 'Deleted')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {t(
                          countryCode === 'AT'
                            ? 'companyDetail.deletedTooltipAustria'
                            : 'companyDetail.deletedTooltip',
                          'This company has been deleted from the Handelsregister.',
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
              {legalStatus === 'insolvent' && (
                <>
                  <span className="text-muted-foreground/50 text-sm leading-none select-none">
                    |
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center rounded-full bg-orange-500/10 border border-orange-500/30 px-1.5 py-0.5 font-medium text-orange-600 dark:text-orange-400 cursor-default">
                          {i18n.language === 'de'
                            ? 'Insolvenzverfahren'
                            : 'Insolvency Proceedings'}
                        </span>
                      </TooltipTrigger>
                      {insolvencyRecords && insolvencyRecords.length > 0 && (
                        <TooltipContent side="bottom" className="text-xs">
                          <div className="flex flex-col gap-0.5">
                            {[...insolvencyRecords]
                              .sort((a, b) =>
                                b.publish_date.localeCompare(a.publish_date),
                              )
                              .map((r, i) => (
                                <span key={i} className="flex gap-1.5">
                                  <span className="tabular-nums shrink-0">
                                    {isoToGerman(r.publish_date)}:
                                  </span>
                                  <span>
                                    {t(
                                      `companyHistory.insolvencyCategory.${r.category}`,
                                      { defaultValue: r.category },
                                    )}
                                  </span>
                                </span>
                              ))}
                          </div>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
              {legalStatus === 'in_liquidation' && (
                <>
                  <span className="text-muted-foreground/50 text-sm leading-none select-none">
                    |
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center rounded-full bg-orange-500/10 border border-orange-500/30 px-1.5 py-0.5 font-medium text-orange-600 dark:text-orange-400 cursor-default">
                          {t('companyDetail.inLiquidationLabel', 'In Liquidation')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {i18n.language === 'de'
                          ? 'Die Gesellschaft ist aufgelöst und befindet sich in Liquidation.'
                          : 'This company is dissolved and currently in liquidation.'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
              {/* Auflösung on record but no Liquidator in office — the
                  winding-up is over (or a terminated insolvency left the
                  company dissolved). Distinct from "In Liquidation", which
                  means the process is still running. */}
              {legalStatus === 'dissolved' && (
                <>
                  <span className="text-muted-foreground/50 text-sm leading-none select-none">
                    |
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center rounded-full bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 font-medium text-amber-700 dark:text-amber-400 cursor-default">
                          {t('companyDetail.dissolvedLabel', 'Dissolved')}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {i18n.language === 'de'
                          ? 'Die Gesellschaft ist aufgelöst. Es ist kein Liquidator mehr bestellt.'
                          : 'This company is dissolved. No liquidator is currently in office.'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </span>
            {parentCompany &&
              (() => {
                const parentFlagUrl = flagUrlForCountry(
                  parentCompany.country_code,
                );
                return (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span className="opacity-70">
                      {t('companyDetail.parent')}:
                    </span>
                    {parentFlagUrl ? (
                      <img
                        src={parentFlagUrl}
                        alt={parentCompany.country_code ?? ''}
                        className="h-3.5 w-3.5 shrink-0 rounded-sm object-contain"
                      />
                    ) : (
                      <Building2 className="h-3.5 w-3.5 shrink-0 opacity-70" />
                    )}
                    {parentCompany.eu_id && parentCompany.register_court ? (
                      <LocaleLink
                        href={generateCompanySlug(
                          {
                            name: parentCompany.name,
                            registerCourt: parentCompany.register_court,
                            registerType:
                              parentCompany.register_type ?? undefined,
                            registerId: parentCompany.register_id ?? undefined,
                          },
                          locale,
                        )}
                        className="opacity-70 hover:underline"
                      >
                        {parentCompany.name}
                      </LocaleLink>
                    ) : (
                      <span className="opacity-70">{parentCompany.name}</span>
                    )}
                  </div>
                );
              })()}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <AddToListMenu
            euIds={[basicData.eu_id]}
            className="h-auto min-h-8 rounded-full text-xs"
          />
          <WatchlistButton euId={basicData.eu_id} />
          {headerAction}
        </div>
      </CardHeader>
      {!compact && <div className="border-b border-border" />}
      {statistics.some((stat) => stat.number !== '—') && (
        <>
          <div className={compact ? "border-b border-border px-5 py-4 grid grid-cols-2 gap-4" : "border-b border-border px-5 py-4 flex justify-start gap-4 sm:gap-8"}>
            {statistics
              .filter((stat) => stat.number !== '—')
              .map((stat, idx) => (
                <div key={idx} className="flex flex-col items-start gap-0.5">
                  <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground leading-none">
                    {stat.label}
                  </span>
                  <span className="text-xs sm:text-sm font-bold text-foreground">
                    {stat.number}
                  </span>
                </div>
              ))}
          </div>
        </>
      )}

      <CardContent className="space-y-6 pt-6 lg:pb-0 lg:grow-0">
        {/* WARNING: overrides.about_us from company owners has HIGHEST PRIORITY
            over AI summary and register Gegenstand. */}
        <AboutSection
          ownerAboutUs={basicData?.overrides?.about_us}
          websiteSummary={localizedWebsiteSummary}
          aiSummary={localizedAiSummary}
          ownershipSentence={
            [ownershipSentence, sizeSentence].filter(Boolean).join(' ') || null
          }
          gegenstand={registerData?.gegenstand ?? basicData?.gegenstand}
          factsLead={factsLead}
          isLoading={isLoadingBackground}
        >
          {naceCodes.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {naceCodes.map((code, idx) => (
                <LocaleLink
                  key={idx}
                  href={generateIndustryUrl(code.code, code.description)}
                  className="hover:opacity-80 transition-opacity"
                >
                  <span className="inline-flex items-center gap-1 whitespace-normal text-left text-[10px] sm:text-xs text-foreground border border-border rounded-md px-1.5 py-0.5 bg-muted/30 dark:bg-muted/50 dark:border-border/70">
                    <span>
                      {code.section
                        ? `${code.section}.${code.code}`
                        : code.code}{' '}
                      - {code.description}
                    </span>
                    <ExternalLink size={10} className="shrink-0 opacity-60" />
                  </span>
                </LocaleLink>
              ))}
            </div>
          )}
        </AboutSection>
      </CardContent>
      </div>

      {/* The rest, from the network on. Split below lg it opens with no rule
          of its own: the card above it already draws one. */}
      <div className={split ? cn(split.rest, 'lg:contents') : 'contents'}>
      <CardContent
        className={cn(
          'space-y-6 pt-6',
          split && 'max-lg:pt-0 max-lg:[&>*:first-child]:border-t-0',
        )}
      >
        {networkData && !isDeleted && (
          <>
            <div className="border-t" />

            <div className="h-[228px]">
              <CompanyNetworkDiagram
                euId={euId}
                companyName={basicData?.display_name}
                initialData={networkData}
                reloadToken={networkReloadToken}
                focalFaviconUrl={
                  getPublicStorageUrl(basicData?.logo_url) ??
                  getPublicStorageUrl(basicData?.favicon_url)
                }
              />
            </div>
          </>
        )}

        {businessBranches.filter((b) => b.revenue != null).length > 1 &&
          (() => {
            const withRevenue = businessBranches.filter(
              (b) => b.revenue != null,
            );
            const parentNames = new Set(
              withRevenue.map((b) => b.parent_name).filter(Boolean),
            );
            // Top-level: either has no parent_name, or its parent_name is not itself a branch name
            const branchNames = new Set(withRevenue.map((b) => b.name));
            const topLevel = withRevenue.filter(
              (b) => !b.parent_name || !branchNames.has(b.parent_name),
            );
            const children = (parentName: string) =>
              withRevenue.filter((b) => b.parent_name === parentName);
            const fmtBranch = (v: number, unit?: string | null) => {
              if (unit) {
                return `${Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(v)} ${unit}`;
              }
              return Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
                notation: 'compact',
                maximumFractionDigits: 1,
              }).format(v);
            };
            return (
              <div className="space-y-3 pt-4 border-t">
                <h2 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground">
                  {t('companyDetail.businessSectors')}
                </h2>
                <div className="flex flex-col gap-1">
                  {topLevel.map((branch, idx) => (
                    <div key={idx}>
                      <div className="flex items-center justify-between gap-4 text-xs sm:text-sm">
                        <span
                          className={
                            parentNames.has(branch.name)
                              ? 'font-medium text-foreground'
                              : 'text-foreground'
                          }
                        >
                          {branch.name}
                        </span>
                        <span className="text-muted-foreground font-medium shrink-0">
                          {fmtBranch(branch.revenue!, branch.revenue_unit)}
                          {branch.percentage_of_total != null && (
                            <span className="ml-1.5 text-xs text-muted-foreground">
                              ({branch.percentage_of_total.toFixed(1)}%)
                            </span>
                          )}
                        </span>
                      </div>
                      {children(branch.name).map((child, cidx) => (
                        <div
                          key={cidx}
                          className="flex items-center justify-between gap-4 text-xs sm:text-sm pl-4 mt-0.5"
                        >
                          <span className="text-muted-foreground">
                            {child.name}
                          </span>
                          <span className="text-muted-foreground font-medium shrink-0">
                            {fmtBranch(child.revenue!, child.revenue_unit)}
                            {child.percentage_of_total != null && (
                              <span className="ml-1.5 text-xs text-muted-foreground">
                                ({child.percentage_of_total.toFixed(1)}%)
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

        {products.length > 0 && (
          <div className="space-y-3 pt-4 border-t">
            <h2 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground">
              {t('companyDetail.productsAndSubSectors')}
            </h2>
            <div className="flex flex-wrap gap-2">
              {products.slice(0, 8).map((product, idx) => (
                <LocaleLink
                  key={idx}
                  href={`${localizePathname('/search', locale as Locale)}?tag=${encodeURIComponent(product)}`}
                  className="hover:opacity-80 transition-opacity"
                >
                  <Badge
                    variant="secondary"
                    className="cursor-pointer border border-border bg-muted/60 text-foreground dark:bg-muted/50 flex items-center gap-1"
                  >
                    {product}
                    <ExternalLink size={10} className="shrink-0 opacity-60" />
                  </Badge>
                </LocaleLink>
              ))}
            </div>
          </div>
        )}

        {((financialsData?.risk_keywords?.length ?? 0) > 0 ||
          (financialsData?.opportunity_keywords?.length ?? 0) > 0) && (
          <div className="space-y-3 pt-4 border-t">
            <h2 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground">
              {t('companyDetail.risksAndOpportunities')}
            </h2>
            <div className="space-y-2">
              {(financialsData?.opportunity_keywords?.length ?? 0) > 0 && (
                <div className="flex flex-wrap gap-2">
                  {financialsData?.opportunity_keywords
                    ?.slice(0, 8)
                    .map((keyword, idx) => (
                      <LocaleLink
                        key={`opp-${idx}`}
                        href={`${localizePathname('/search', locale as Locale)}?tag=${encodeURIComponent(keyword)}`}
                        className="hover:opacity-80 transition-opacity"
                      >
                        <Badge
                          variant="success"
                          appearance="light"
                          size="sm"
                          className="cursor-pointer flex items-center gap-1"
                        >
                          {keyword}
                          <ExternalLink
                            size={10}
                            className="shrink-0 opacity-60"
                          />
                        </Badge>
                      </LocaleLink>
                    ))}
                </div>
              )}
              {(financialsData?.risk_keywords?.length ?? 0) > 0 && (
                <div className="flex flex-wrap gap-2">
                  {financialsData?.risk_keywords
                    ?.slice(0, 8)
                    .map((keyword, idx) => (
                      <LocaleLink
                        key={`risk-${idx}`}
                        href={`${localizePathname('/search', locale as Locale)}?tag=${encodeURIComponent(keyword)}`}
                        className="hover:opacity-80 transition-opacity"
                      >
                        <Badge
                          variant="destructive"
                          appearance="light"
                          size="sm"
                          className="cursor-pointer flex items-center gap-1"
                        >
                          {keyword}
                          <ExternalLink
                            size={10}
                            className="shrink-0 opacity-60"
                          />
                        </Badge>
                      </LocaleLink>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}

        {(localizedAiSummary ||
          localizedWebsiteSummary ||
          basicData?.overrides?.about_us) && (
          <p className="text-xs text-muted-foreground pt-2" data-nosnippet>
            <em>
              {usingWebsiteSummary && websiteCapturedDate
                ? t('companyDetail.aiDisclaimerWebsite', {
                    date: websiteCapturedDate,
                  })
                : t('companyDetail.aiDisclaimer')}{' '}
              <LocaleLink
                href="/datenqualitaet"
                className="underline hover:text-foreground transition-colors"
              >
                {t('companyDetail.dataQualityNotice')}
              </LocaleLink>
              .
            </em>{' '}
            <LocaleLink
              href={`/contact${basicData?.display_name ? `?company=${encodeURIComponent(basicData.display_name)}` : ''}`}
              className="underline hover:text-foreground transition-colors"
            >
              {t('companyDetail.claimNoteLink')}
            </LocaleLink>
          </p>
        )}
        {countryCode !== 'CH' && (
          <SourceAttribution
            source={countryCode === 'AT' ? 'firmenbuch' : 'handelsregister'}
            registerCourt={basicData.register_court}
            registerId={formatRegisterNumber(
              basicData.register_type,
              basicData.register_id,
            )}
            capturedAt={basicData.updated_at}
          />
        )}
      </CardContent>
      </div>
    </Card>
  );
}

/**
 * Subset of CompanyRegisterResponse fields used by the profile section.
 * Typed via import to avoid a direct dependency on the full response shape.
 */
type CompanyRegisterResponseForProfile =
  import('@/lib/backend-api').CompanyRegisterResponse;
